"use client";

import { useState } from "react";
import {
  CopyButton,
  Panel,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SMALL,
  HINT,
  LABEL,
  TEXTAREA,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import type { URL_PARSER_STRINGS } from "@/components/tools/url-parser";
import { t } from "@/lib/i18n/interpolate";
import {
  buildUrl,
  type EditableUrl,
  isTrackingName,
  type SpaceStyle,
  toEditable,
  type UrlParts,
} from "@/lib/tool-kit/latex-dev/url-parts";

type Strings = typeof URL_PARSER_STRINGS;

interface Row {
  id: number;
  name: string;
  value: string;
  hasEquals: boolean;
}

const SPACE_STYLES: readonly SpaceStyle[] = ["percent", "plus"];

/** Editable copy of a parsed URL. Mount it with a `key` to start again from a new URL. */
export function UrlRebuilder({
  parts,
  strings,
  fmt,
}: {
  parts: UrlParts;
  strings: Strings;
  fmt: (value: number) => string;
}) {
  const initial = toEditable(parts);
  const [scheme, setScheme] = useState(initial.scheme);
  const [username, setUsername] = useState(initial.username);
  const [password, setPassword] = useState(initial.password);
  const [host, setHost] = useState(initial.host);
  const [port, setPort] = useState(initial.port);
  const [path, setPath] = useState(initial.path);
  const [hash, setHash] = useState(initial.hash);
  const [space, setSpace] = useState<SpaceStyle>("percent");
  const [nextId, setNextId] = useState(initial.params.length);
  const [rows, setRows] = useState<Row[]>(
    initial.params.map((p, id) => ({ id, ...p }))
  );

  const editable: EditableUrl = {
    scheme,
    username,
    password,
    host,
    port,
    path,
    hash,
    params: rows,
  };
  const rebuilt = buildUrl(editable, space);

  function update(id: number, patch: Partial<Row>) {
    setRows((current) =>
      current.map((row) => (row.id === id ? { ...row, ...patch } : row))
    );
  }

  return (
    <Panel title={strings.rebuildHeading}>
      <p className={HINT}>{strings.rebuildHint}</p>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          id="up-scheme"
          label={strings.partNames.scheme}
          onChange={setScheme}
          value={scheme}
        />
        <TextField
          id="up-host"
          label={strings.partNames.host}
          onChange={setHost}
          value={host}
        />
        <TextField
          id="up-user"
          label={strings.partNames.username}
          onChange={setUsername}
          value={username}
        />
        <TextField
          id="up-pass"
          label={strings.partNames.password}
          onChange={setPassword}
          value={password}
        />
        <TextField
          id="up-port"
          label={strings.partNames.port}
          onChange={setPort}
          value={port}
        />
        <TextField
          id="up-path"
          label={strings.partNames.path}
          onChange={setPath}
          value={path}
        />
        <TextField
          id="up-hash"
          label={strings.partNames.fragment}
          onChange={setHash}
          value={hash}
        />
      </div>
      <h3 className={WIDGET_H3}>{strings.paramsHeading}</h3>
      <div className="space-y-3">
        {rows.map((row, index) => (
          <div
            className="grid items-end gap-3 sm:grid-cols-[1fr_1fr_auto]"
            key={row.id}
          >
            <TextField
              id={`up-name-${row.id}`}
              label={t(strings.paramName, { n: fmt(index + 1) })}
              onChange={(value) => update(row.id, { name: value })}
              value={row.name}
            />
            <TextField
              id={`up-value-${row.id}`}
              label={t(strings.paramValue, { n: fmt(index + 1) })}
              onChange={(value) => update(row.id, { value })}
              value={row.value}
            />
            <button
              className={BUTTON_SMALL}
              onClick={() =>
                setRows((current) => current.filter((r) => r.id !== row.id))
              }
              type="button"
            >
              {strings.removeRow}
            </button>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SMALL}
          onClick={() => {
            setRows((current) => [
              ...current,
              { id: nextId, name: "", value: "", hasEquals: true },
            ]);
            setNextId(nextId + 1);
          }}
          type="button"
        >
          {strings.addRow}
        </button>
        <button
          className={BUTTON_SMALL}
          onClick={() =>
            setRows((current) => current.filter((r) => !isTrackingName(r.name)))
          }
          type="button"
        >
          {strings.removeTracking}
        </button>
        <button
          className={BUTTON_SMALL}
          onClick={() =>
            setRows((current) =>
              [...current].sort((a, b) => a.name.localeCompare(b.name))
            )
          }
          type="button"
        >
          {strings.sortRows}
        </button>
      </div>
      <SelectField
        id="up-space"
        label={strings.spaceLabel}
        onChange={setSpace}
        options={SPACE_STYLES.map((value) => ({
          value,
          label: strings.spaceStyles[value],
        }))}
        value={space}
      />
      <div className="space-y-2">
        <label className={LABEL} htmlFor="up-rebuilt">
          {strings.rebuiltLabel}
        </label>
        <textarea
          className={TEXTAREA}
          id="up-rebuilt"
          readOnly
          rows={3}
          spellCheck={false}
          value={rebuilt}
        />
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyRebuilt}
          text={rebuilt}
        />
      </div>
    </Panel>
  );
}
